"use client";

import { useState, useEffect } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DateTime, Duration } from "luxon";

type CountdownValues = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

type CountdownProps = {
  countdownIsoDate: string;
  className?: string;
  cellClassName?: string;
  dividerClassName?: string;
};

const Countdown: React.FC<CountdownProps> = ({
  countdownIsoDate,
  className,
  cellClassName,
  dividerClassName,
}) => {
  const [countdown, setCountdown] = useState<CountdownValues>({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    const targetDate = DateTime.fromISO(countdownIsoDate);

    const updateCountdown = () => {
      const now = DateTime.now();
      const diff = targetDate.diff(now);

      if (diff.milliseconds <= 0) {
        setCountdown({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        return;
      }

      const duration = Duration.fromObject(diff.toObject()).shiftTo(
        "days",
        "hours",
        "minutes",
        "seconds",
      );

      const padZero = (num: number): string => {
        return num < 10 ? `0${num}` : num.toString();
      };

      setCountdown({
        days: padZero(duration.days),
        hours: padZero(duration.hours),
        minutes: padZero(duration.minutes),
        seconds: padZero(Math.floor(duration.seconds)),
      });
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [countdownIsoDate]);

  const renderCell = (value: string, label: string) => (
    <div className={twMerge(clsx("flex min-w-18 flex-col items-center", cellClassName))}>
      <span className="text-4xl font-bold leading-[1.2] md:text-5xl lg:text-6xl">{value}</span>
      <span>{label}</span>
    </div>
  );

  const renderDivider = () => (
    <div
      className={twMerge(clsx("hidden w-px bg-background-alternative sm:block", dividerClassName))}
    />
  );

  return (
    <div
      className={twMerge(
        clsx(
          "flex flex-wrap justify-center gap-4 border border-border-primary px-4 py-4 sm:flex-nowrap sm:px-6",
          className,
        ),
      )}
    >
      {renderCell(countdown.days, "Days")}
      {renderDivider()}
      {renderCell(countdown.hours, "Hours")}
      {renderDivider()}
      {renderCell(countdown.minutes, "Mins")}
      {renderDivider()}
      {renderCell(countdown.seconds, "Secs")}
    </div>
  );
};

Countdown.displayName = "Countdown";

export { Countdown };
